import { FileText, RotateCw, Upload } from "lucide-react";
import { useRef, useState } from "react";
import type { Me } from "../../lib/me";
import { RoleButton } from "../RequireRole";
import { byFile, canReadAgain, freeOnlyNote, isBusy, sheetProgress } from "./flow";
import type { Sheet } from "./pidApi";
import { TableScroll } from "../TableScroll";

/** Pick a P&ID PDF and send it. Engineers only; a viewer sees the box disabled with the reason. */
export function UploadBox({ me, busy, onUpload }: { me: Me | null | undefined; busy: boolean; onUpload: (file: File) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [name, setName] = useState<string | null>(null);
  const pick = (file: File | undefined) => {
    if (!file) return;
    setName(file.name);
    onUpload(file);
  };
  return (
    <div className="card" style={{ padding: 16 }} data-pid-upload>
      <input
        ref={input}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        aria-label="P&ID PDF"
        onChange={(e) => pick(e.target.files?.[0])}
      />
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 600 }}>Upload a P&ID set</div>
          <p className="muted" style={{ fontSize: 13 }}>One PDF, up to 200 pages. Every page becomes a sheet and is read at 300 dpi.</p>
        </div>
        <RoleButton role="engineer" me={me} disabled={busy} onClick={() => input.current?.click()}>
          <span className="row" style={{ gap: 6 }}><Upload size={16} aria-hidden="true" /> {busy ? `Uploading ${name ?? ""}` : "Choose PDF"}</span>
        </RoleButton>
      </div>
    </div>
  );
}

/** Every sheet of the package by file, with how far its reading has got. */
export function SheetList({ sheets, selected, me, onSelect, onReadAgain }: {
  sheets: Sheet[];
  selected: string | null;
  me: Me | null | undefined;
  onSelect: (id: string) => void;
  onReadAgain: (id: string) => void;
}) {
  return (
    <div className="stack" style={{ gap: 12 }} data-pid-sheets>
      {byFile(sheets).map((group) => (
        <section key={group.document_id} className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="row" style={{ padding: "10px 14px", borderBottom: "1px solid var(--color-border)" }}>
            <FileText size={16} aria-hidden="true" />
            <span style={{ fontWeight: 600 }}>{group.filename}</span>
            <span className="muted" style={{ fontSize: 13 }}>{group.sheets.length} sheet{group.sheets.length === 1 ? "" : "s"}</span>
          </div>
          <TableScroll label="P&ID sheets">
          <table>
            <tbody>
              {group.sheets.map((s) => {
                const p = sheetProgress(s);
                const note = freeOnlyNote(s);
                return (
                  <tr key={s.id} data-sheet={s.id} aria-selected={selected === s.id}
                    style={selected === s.id ? { background: "var(--color-surface-alt)" } : undefined}>
                    <td style={{ width: 70 }} className="mono">page {s.page}</td>
                    <td>
                      <div className="progress" role="progressbar" aria-label="Sheet reading progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={p.pct}><i style={{ width: `${p.pct}%` }} /></div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{p.label}</div>
                      {note && <div style={{ fontSize: 12, marginTop: 2, color: "var(--color-review)" }}>{note}</div>}
                    </td>
                    <td style={{ width: 230, textAlign: "right" }}>
                      <span className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                        {s.status === "ready" && (
                          <button className="ghost" onClick={() => onSelect(s.id)}>Open</button>
                        )}
                        {canReadAgain(s) && (
                          <RoleButton role="engineer" me={me} className="ghost" onClick={() => onReadAgain(s.id)}>
                            <span className="row" style={{ gap: 4 }}><RotateCw size={14} aria-hidden="true" /> Read again</span>
                          </RoleButton>
                        )}
                        {isBusy(s) && <span className="pill info">{s.status}</span>}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </TableScroll>
        </section>
      ))}
    </div>
  );
}
